import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, Spin, Alert, Button, Modal, message, Image } from 'antd';
import {
  PrinterOutlined,
  CloseCircleOutlined,
  CreditCardOutlined,
  PhoneOutlined,
  MailOutlined,
  ShopOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import api from '../../services/api';
import { formatVnDate, formatVnTime } from '../../utils/vnDateTime';
import { formatOrderItemSubtitle } from '../../utils/orderItemOptionsFormatter';
import {
  adminOrderStatusLabel,
  adminOrderStatusVariant,
} from '../../utils/orderStatus';

interface OrderItemRow {
  food_name: string;
  quantity: number;
  price: number;
  food_image?: string | null;
  selected_variant?: string | null;
  selected_add_ons?: unknown;
  selected_configurations?: unknown;
  customization_display?: string | null;
  special_instructions?: string | null;
}

interface OrderDetail {
  id: number;
  order_number: string;
  total: number;
  status: string;
  created_at: string;
  updated_at?: string;
  payment_method?: string | null;
  special_instructions?: string | null;
  delivery_notes?: string | null;
  subtotal?: number | null;
  delivery_fee?: number | null;
  discount?: number | null;
  delivery_address?: string | null;
  delivery_phone?: string | null;
  delivery_name?: string | null;
  confirmed_at?: string | null;
  delivered_at?: string | null;
  delivery_proof_url?: string | null;
  delivery_proof_uploaded_at?: string | null;
  cancelled_at?: string | null;
  cancelled_by?: string | null;
  cancelled_reason_code?: string | null;
  cancellation_reason?: string | null;
  cancelled_reason_text?: string | null;
  customerId?: number;
  customer_id?: number;
  customerid?: number;
  customerName?: string;
  customer_phone?: string;
  customer_email?: string;
  customer_address?: string;
  restaurantName?: string;
  restaurantAddress?: string;
  merchantId?: number;
  merchant_id?: number;
  merchantid?: number;
  items?: OrderItemRow[];
}

function formatCurrency(value: number | undefined | null): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

function orderIdDisplay(orderNumber: string | undefined): string {
  return orderNumber ? `#${orderNumber}` : '–';
}

function statusLabel(status: string | undefined): string {
  return adminOrderStatusLabel(status);
}

function statusBadgeVariant(status: string | undefined) {
  return adminOrderStatusVariant(status);
}

function formatOrderNote(data: OrderDetail): string {
  const parts: string[] = [];
  const orderNote = (
    data.special_instructions ||
    data.delivery_notes ||
    ''
  ).trim();
  if (orderNote) parts.push(orderNote);

  for (const item of data.items || []) {
    const note = item.special_instructions?.trim();
    if (!note) continue;
    const name = item.food_name?.trim() || 'Item';
    parts.push(`${name}: ${note}`);
  }

  return parts.length > 0 ? parts.join('\n') : '–';
}

function cancelReasonCodeLabel(code: string | undefined | null): string {
  const c = (code || '').toLowerCase();
  const map: Record<string, string> = {
    system_timeout: 'Trễ',
    system_pending_timeout: 'Không xác nhận kịp',
    out_of_stock: 'Hết món',
    closed: 'Đóng cửa',
    merchant_fault: 'Quá tải',
    user_request: 'Khách hàng hủy',
    admin_override: 'Admin hủy',
    other: 'Khác',
  };
  return map[c] || code || '–';
}

function cancelReasonText(data: OrderDetail): string {
  const text = (
    data.cancellation_reason ||
    data.cancelled_reason_text ||
    ''
  ).trim();
  if (text) return text;
  return cancelReasonCodeLabel(data.cancelled_reason_code);
}

function canAdminCancelOrder(status: string | undefined): boolean {
  const s = (status || '').toLowerCase();
  return !['delivered', 'cancelled'].includes(s);
}

export default function OrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (!id) return;
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/orders/${id}`);
        const raw = res.data?.data;
        // API returns { order, items }; normalize to a single object for the UI
        const order = raw?.order || {};
        const items = Array.isArray(raw?.items) ? raw.items : [];
        setData({ ...order, items });
        setError(null);
      } catch (e: unknown) {
        const err = e as {
          response?: { data?: { message?: string }; status?: number };
          message?: string;
        };
        setError(
          err.response?.data?.message || err.message || 'Failed to load order'
        );
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  if (loading && !data) {
    return (
      <div className="order-detail-page">
        <div className="order-detail-loading">
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="order-detail-page">
        <Alert
          type="error"
          message={error}
          showIcon
          action={
            <Button size="small" onClick={() => navigate('/orders')}>
              Back to Orders
            </Button>
          }
          className="order-detail-alert"
        />
      </div>
    );
  }

  if (!data) return null;

  const subtotalFromApi = Number(data.subtotal);
  const subtotal =
    Number.isFinite(subtotalFromApi) && subtotalFromApi > 0
      ? subtotalFromApi
      : (data.items || []).reduce((sum, it) => {
          const p = Number(it.price);
          const q = Number(it.quantity);
          return sum + (Number.isFinite(p) && Number.isFinite(q) ? p * q : 0);
        }, 0);
  const deliveryFee = Number(data.delivery_fee);
  const discountFromApi = Number(data.discount);
  const grandTotal = Number(data.total);
  const statusVariant = statusBadgeVariant(data.status);
  const orderIdStr = orderIdDisplay(data.order_number);
  const allowCancel = canAdminCancelOrder(data.status);
  const deliveryAddress =
    (data.delivery_address || '').trim() ||
    (data.customer_address || '').trim() ||
    '–';

  const handleCancelOrder = () => {
    if (!id || !allowCancel) return;
    Modal.confirm({
      title: 'Hủy đơn hàng',
      content: 'Bạn có chắc muốn hủy đơn hàng này?',
      okText: 'Hủy đơn',
      okType: 'danger',
      cancelText: 'Không',
      onOk: async () => {
        try {
          setCancelling(true);
          await api.put(`/orders/${id}/status`, {
            status: 'cancelled',
            is_admin_override: true,
            reason_code: 'admin_override',
          });
          message.success('Đã hủy đơn hàng');
          const res = await api.get(`/orders/${id}`);
          const raw = res.data?.data;
          const order = raw?.order || {};
          const items = Array.isArray(raw?.items) ? raw.items : [];
          setData({ ...order, items });
        } catch (e: unknown) {
          const err = e as {
            response?: { data?: { message?: string }; status?: number };
            message?: string;
          };
          message.error(
            err.response?.data?.message ||
              err.message ||
              'Không thể hủy đơn hàng'
          );
        } finally {
          setCancelling(false);
        }
      },
    });
  };

  return (
    <div className="order-detail-page">
      <header className="order-detail-header">
        <nav className="order-detail-breadcrumb">
          <Link to="/orders">Orders</Link>
          <span className="order-detail-breadcrumb-sep">&gt;</span>
          <span className="order-detail-breadcrumb-current">{orderIdStr}</span>
        </nav>
        <h1 className="order-detail-title">Order Details</h1>
        <div className="order-detail-actions">
          <Button
            icon={<PrinterOutlined />}
            className="order-detail-btn-print"
            onClick={() => window.print()}
          >
            Print Invoice
          </Button>
          <Button
            danger
            icon={<CloseCircleOutlined />}
            className="order-detail-btn-cancel"
            disabled={!allowCancel}
            loading={cancelling}
            onClick={handleCancelOrder}
            title={
              !allowCancel
                ? 'Không thể hủy đơn đã giao hoặc đã hủy.'
                : undefined
            }
          >
            Cancel Order
          </Button>
        </div>
      </header>

      <div className="order-detail-grid">
        <div className="order-detail-col-left">
          <Card className="order-detail-card order-detail-summary-card">
            <div className="order-detail-card-head">
              <h2 className="order-detail-card-title">SUMMARY</h2>
              <span
                className={`order-detail-status-badge order-detail-status-${statusVariant}`}
              >
                <span className="order-detail-status-dot" />
                {statusLabel(data.status)}
              </span>
            </div>
            <dl className="order-detail-summary-list">
              <div className="order-detail-summary-row">
                <dt>Order ID</dt>
                <dd>
                  <strong>{orderIdStr}</strong>
                </dd>
              </div>
              <div className="order-detail-summary-row">
                <dt>Order Number (DB)</dt>
                <dd>
                  <code>{data.order_number || '–'}</code>
                </dd>
              </div>
              <div className="order-detail-summary-row">
                <dt>Date Placed</dt>
                <dd>
                  <span>{formatVnDate(data.created_at, 'MMM D, YYYY')}</span>
                  <span className="order-detail-summary-time">
                    at {formatVnTime(data.created_at, 'h:mm A')}
                  </span>
                </dd>
              </div>
              <div className="order-detail-summary-row">
                <dt>Payment Method</dt>
                <dd className="order-detail-payment">
                  <CreditCardOutlined className="order-detail-payment-icon" />
                  <span>{data.payment_method || '–'}</span>
                </dd>
              </div>
              <div className="order-detail-summary-row">
                <dt>Order Note</dt>
                <dd className="order-detail-note order-detail-note-multiline">
                  {formatOrderNote(data)}
                </dd>
              </div>
              {(data.status || '').toLowerCase() === 'delivered' && (
                <>
                  <div className="order-detail-summary-row">
                    <dt>Delivered At</dt>
                    <dd>
                      {data.delivered_at
                        ? `${formatVnDate(
                            data.delivered_at,
                            'MMM D, YYYY'
                          )} at ${formatVnTime(data.delivered_at, 'h:mm A')}`
                        : '–'}
                    </dd>
                  </div>
                  <div className="order-detail-summary-row">
                    <dt>Delivery Proof</dt>
                    <dd>
                      {data.delivery_proof_url ? (
                        <Image
                          src={data.delivery_proof_url}
                          alt="Delivery proof"
                          width={160}
                          style={{ borderRadius: 8 }}
                        />
                      ) : (
                        '–'
                      )}
                    </dd>
                  </div>
                </>
              )}
              {(data.status || '').toLowerCase() === 'cancelled' && (
                <>
                  <div className="order-detail-summary-row">
                    <dt>Cancelled At</dt>
                    <dd>
                      {data.cancelled_at
                        ? `${formatVnDate(
                            data.cancelled_at,
                            'MMM D, YYYY'
                          )} at ${formatVnTime(data.cancelled_at, 'h:mm A')}`
                        : '–'}
                    </dd>
                  </div>
                  <div className="order-detail-summary-row">
                    <dt>Cancelled By</dt>
                    <dd>{data.cancelled_by || '–'}</dd>
                  </div>
                  <div className="order-detail-summary-row">
                    <dt>Cancel Reason Code</dt>
                    <dd>{cancelReasonCodeLabel(data.cancelled_reason_code)}</dd>
                  </div>
                  <div className="order-detail-summary-row">
                    <dt>Cancel Reason</dt>
                    <dd className="order-detail-note">
                      {cancelReasonText(data)}
                    </dd>
                  </div>
                </>
              )}
            </dl>
          </Card>
        </div>

        <div className="order-detail-col-center">
          <Card className="order-detail-card order-detail-items-card">
            <h2 className="order-detail-card-title">ORDER ITEMS</h2>
            <div className="order-detail-items-table-wrap">
              <table className="order-detail-items-table">
                <thead>
                  <tr>
                    <th className="order-detail-items-th-details">
                      Item Details
                    </th>
                    <th className="order-detail-items-th-qty">Qty</th>
                    <th className="order-detail-items-th-price">Unit Price</th>
                    <th className="order-detail-items-th-total">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.items || []).map((item, idx) => {
                    const p = Number(item.price);
                    const q = Number(item.quantity);
                    const rowTotal =
                      Number.isFinite(p) && Number.isFinite(q) ? p * q : 0;
                    return (
                      <tr key={`${item.food_name}-${idx}`}>
                        <td className="order-detail-items-td-details">
                          <div className="order-detail-item-cell">
                            {item.food_image ? (
                              <img
                                src={item.food_image}
                                alt=""
                                className="order-detail-item-thumb"
                              />
                            ) : (
                              <div className="order-detail-item-thumb order-detail-item-thumb-placeholder" />
                            )}
                            <div className="order-detail-item-info">
                              <span className="order-detail-item-name">
                                {item.food_name || '–'}
                              </span>
                              <span className="order-detail-item-subtext">
                                {formatOrderItemSubtitle(item) || '–'}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="order-detail-items-td-qty">
                          {item.quantity}
                        </td>
                        <td className="order-detail-items-td-price">
                          {formatCurrency(item.price)}
                        </td>
                        <td className="order-detail-items-td-total">
                          {formatCurrency(rowTotal)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="order-detail-totals">
              <div className="order-detail-totals-row">
                <span>Subtotal</span>
                <strong>{formatCurrency(subtotal)}</strong>
              </div>
              {Number.isFinite(deliveryFee) && deliveryFee > 0 && (
                <div className="order-detail-totals-row">
                  <span>Delivery Fee</span>
                  <strong>{formatCurrency(deliveryFee)}</strong>
                </div>
              )}
              {Number.isFinite(discountFromApi) && discountFromApi > 0 && (
                <div className="order-detail-totals-row order-detail-totals-voucher">
                  <span>Discount</span>
                  <strong className="order-detail-voucher-value">
                    {formatCurrency(-discountFromApi)}
                  </strong>
                </div>
              )}
              <div className="order-detail-totals-row order-detail-totals-grand">
                <span>Grand Total</span>
                <strong className="order-detail-grand-value">
                  {formatCurrency(
                    Number.isFinite(grandTotal) ? grandTotal : subtotal
                  )}
                </strong>
              </div>
            </div>
          </Card>
        </div>

        <div className="order-detail-col-right">
          <Card className="order-detail-card order-detail-customer-card">
            <h2 className="order-detail-card-title">CUSTOMER</h2>
            <div className="order-detail-customer-avatar-wrap">
              <div className="order-detail-customer-avatar">
                {data.customerName
                  ? data.customerName.trim().slice(0, 2).toUpperCase()
                  : '?'}
              </div>
              <div className="order-detail-customer-meta">
                <strong className="order-detail-customer-name">
                  {data.customerName || '–'}
                </strong>
                <span className="order-detail-customer-joined">–</span>
              </div>
            </div>
            <div className="order-detail-contact-list">
              <div className="order-detail-contact-row">
                <PhoneOutlined className="order-detail-contact-icon" />
                <span>{data.customer_phone || '–'}</span>
              </div>
              <div className="order-detail-contact-row">
                <MailOutlined className="order-detail-contact-icon" />
                <span>{data.customer_email || '–'}</span>
              </div>
              <div className="order-detail-contact-row">
                <EnvironmentOutlined className="order-detail-contact-icon" />
                <span>{deliveryAddress}</span>
              </div>
            </div>
          </Card>

          <Card className="order-detail-card order-detail-merchant-card">
            <h2 className="order-detail-card-title">MERCHANT</h2>
            <div className="order-detail-merchant-meta">
              <ShopOutlined className="order-detail-merchant-icon" />
              <div>
                <strong className="order-detail-merchant-name">
                  {data.restaurantName || '–'}
                </strong>
                <span className="order-detail-merchant-branch">–</span>
              </div>
            </div>
            <div className="order-detail-contact-list">
              <div className="order-detail-contact-row">
                <EnvironmentOutlined className="order-detail-contact-icon" />
                <span>{data.restaurantAddress || '–'}</span>
              </div>
              <div className="order-detail-contact-row">
                <PhoneOutlined className="order-detail-contact-icon" />
                <span>–</span>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
